import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, setAccessToken } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { Button } from '../ui/Button.js';
import { Logo } from '../ui/Logo.js';

interface VerifyResponse {
  accessToken: string;
  user: { id: string; email: string; name: string; role: string };
}

/**
 * Route: /verify-email?token=...
 *
 * Hit by the link in the welcome email. Calls POST /api/auth/verify-email
 * with the token; on success, the API returns an access token and we sign
 * the user in immediately + redirect to /pipeline.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('error');
      setError('Missing verification token.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api<VerifyResponse>('/api/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        setAccessToken(r.accessToken);
        await refresh();
        setState('success');
        setTimeout(() => navigate('/pipeline', { replace: true }), 1500);
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : 'Verification failed';
        setError(msg);
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refresh, navigate]);

  return (
    <div className="flex h-full items-center justify-center bg-brand-cream px-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-soft">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <Logo variant="full" tone="light" size={44} />
        </div>

        {state === 'verifying' && (
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-brand-navy-800">
              Verifying your email…
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Hang tight, this only takes a second.
            </p>
          </div>
        )}

        {state === 'success' && (
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-emerald-700">
              ✓ Email verified
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Your account is active. Redirecting you to the pipeline…
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="text-center">
            <h2 className="font-display text-2xl font-bold text-rose-700">
              Couldn't verify
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {error === 'verification_expired'
                ? 'This verification link has expired. Sign in to request a new one.'
                : error === 'verification_invalid'
                  ? "We couldn't find that verification link. It may have already been used."
                  : (error ?? 'Verification failed.')}
            </p>
            <Button className="mt-4" onClick={() => navigate('/signin', { replace: true })}>
              Go to sign in
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
